/* eslint-disable react/jsx-no-useless-fragment */
import { Badge } from 'antd';
import { Coordinate, toStringHDMS } from 'ol/coordinate';
import {
  ReactNode, useContext, useLayoutEffect, useState,
} from 'react';
import { useGetLayerStylesQuery } from 'services/geoserver';
import { DTNDisplayRow } from 'services/dtn/types';
import { getColorByStyleAndValue } from 'utils';
import MapViewContext from 'contexts/MapViewContext';
import ProductsContext from 'contexts/ProductsContext';
import styles from './index.module.scss';

const { badge, wrapper } = styles;
interface FeatureProps {
  info: {value: number, style: string, unit: string},
  layerName: string,
  internalName: string,
}

interface DTNFeatureProps {
  layerName: string;
  rows: DTNDisplayRow[];
}

function DTNFeature({ layerName, rows }: DTNFeatureProps) {
  const typeRow = rows.find(
    (r) => typeof r.displayValue === 'string' && Number.isNaN(parseFloat(r.displayValue)),
  );
  const dataRows = typeRow ? rows.filter((r) => r !== typeRow) : rows;
  // If separating the type row left nothing to display, render all rows plainly
  const renderRows = dataRows.length > 0 ? dataRows : rows;

  return (
    <>
      <p><span>{layerName}</span></p>
      {renderRows.map((row) => (
        <p key={row.label}>
          <Badge color={row.color ?? '#F2EFE9'} className={badge} />
          {' '}
          {`${row.label}${dataRows.length > 0 && typeRow ? ` (${typeRow.displayValue})` : ''}: ${row.displayValue}${row.unit ? ` ${row.unit}` : ''}`}
        </p>
      ))}
    </>
  );
}

function Feature({ info, layerName, internalName }: FeatureProps) {
  const { data: styles } = useGetLayerStylesQuery({ layerName: internalName || '' }, { skip: !internalName });

  function formatNumber(num:number) {
    if (num >= 100) {
      return Math.round(num).toString();
    } if (num >= 10) {
      return num.toFixed(1);
    }
    return num.toFixed(2);
  }
  const getColor = (value: number, style: string) => {
    let color = '#F2EFE9';
    if (!value) {
      color = '#F2EFE9';
    } else {
      const styleConfig = styles?.find((s) => s.name === style);
      if (styleConfig) {
        const generatedColor = getColorByStyleAndValue(styleConfig, value);
        if (generatedColor) {
          color = generatedColor;
        }
      }
    }
    return color;
  };
  return (
    <p>
      <Badge color={getColor(info.value, info.style)} className={`${badge}`} />
      {' '}
      <span>{`${layerName}:`}</span>
      {' '}
      {`${info.value != null ? formatNumber(info.value) : ''} ${info.value != null && info.unit ? info.unit : ''}`}
    </p>
  );
}

function FeaturesPopup() {
  const { activeProducts, getProductById } = useContext(ProductsContext);
  const {
    popupContainer, popupInfo, index, mapContainer,
  } = useContext(MapViewContext);
  const [popupPosition, setPopupPosition] = useState<Record<string, string>>({ left: '5px', top: '5px' });

  const getFeatures = () => Object.keys(popupInfo).reduce<ReactNode[]>((acc, cur) => {
    const name = cur.split('&&')[0];
    if (cur !== 'osm' && activeProducts.some((index) => getProductById(index)?.name === name)) {
      const info = popupInfo[cur] as {
        value: number,
        style: string,
        unit: string,
        internalName: string,
        provider?: string,
        dtnRows?: DTNDisplayRow[],
      };
      if (info.provider === 'dtn' && info.dtnRows?.length) {
        acc.push(
          <DTNFeature
            key={`${cur}_dtn`}
            layerName={name}
            rows={info.dtnRows}
          />,
        );
      } else if ((info.value || info.value === 0) && info.provider !== 'dtn') {
        acc.push(
          <Feature key={`${cur}_${info.style}`} layerName={name} info={info} internalName={info.internalName as string} />,
        );
      }
    }
    return acc;
  }, []);

  const completeCoordinatesIfNeeded = (coords: string[]) => coords.map((coord) => {
    if (coord[coord.length - 3] === '°') {
      const splitCoord = coord.split(' ');
      return `${splitCoord[0]} 00′ 00″ ${splitCoord[1]}`;
    }
    return coord;
  });

  const getCoords = () => {
    let coords : string[] | null = toStringHDMS(popupInfo.coordinates as Coordinate).match(/([0-9]| |°|′|″)* (N|S|E|W){1}/g);
    if (coords) {
      coords = completeCoordinatesIfNeeded(coords);
      return (
        <>
          <p>
            <span>Lat:</span>
            {` ${coords[0]}`}
          </p>
          <p>
            <span>Lon:</span>
            {` ${coords[1]}`}
          </p>
        </>
      );
    }
    return null;
  };

  function checkPopupOverlap(x: number, y: number, width: number, height: number) {
    const step = 5;
    const overlappedElements = new Set<Element>();

    let globalX = x;
    let globalY = y;

    const tileWrappers = document.querySelectorAll('.singleTileWrapper');

    if (tileWrappers.length > 0) {
      const container = tileWrappers[index];

      if (container) {
        const rect = container.getBoundingClientRect();
        const containerIndex = index;

        globalX = x + containerIndex * rect.width;
        globalY = y;
      }
    }

    for (let offsetX = 0; offsetX <= width; offsetX += step) {
      for (let offsetY = 0; offsetY <= height; offsetY += step) {
        const element = document.elementFromPoint(globalX + offsetX, globalY + offsetY);
        if (element) {
          overlappedElements.add(element);
        }
      }
    }

    return Array.from(overlappedElements);
  }

  const calculatePosition = (pixel: string[]) => {
    let isOverlappingViewModeMenu = false;
    const position : Record<string, string> = {};
    const margin = '5px';
    const popupSize = {
      width: popupContainer?.current?.clientWidth || 0,
      height: popupContainer?.current?.clientHeight || 0,
    };
    // Use this map instance's own container (not the first <canvas> in the
    // whole document) so the edge check is correct for every tile in tile mode.
    const containerSize = {
      width: mapContainer?.current?.clientWidth || 0,
      height: mapContainer?.current?.clientHeight || 0,
    };
    const [x, y] = pixel || [];
    const parsedX = parseInt(x, 10);
    const parsedY = parseInt(y, 10);

    if (!Number.isNaN(parsedX) && !Number.isNaN(parsedY)) {
      const elements = checkPopupOverlap(parsedX, parsedY, 180, 80);
      isOverlappingViewModeMenu = elements.some((el) => Array.from(el.classList).some((cls) => cls.includes('ViewModeMenu')));
    }

    if (isOverlappingViewModeMenu || (parsedX + popupSize.width) > containerSize.width) {
      position.right = margin;
    } else {
      position.left = margin;
    }

    if ((parsedY + popupSize.height) > containerSize.height) {
      position.bottom = margin;
    } else {
      position.top = margin;
    }

    return position;
  };

  // Recalculate after this popup's own content has been committed to the DOM
  // (not during render, which would still be measuring the *previous*
  // content's size) so the edge/flip check uses the popup's real width/height.
  // `index` is a real dependency (checkPopupOverlap uses it to compute the
  // tile-relative offset); `calculatePosition` itself is intentionally left
  // out since it's redefined on every render and would otherwise re-run the
  // effect unconditionally.
  useLayoutEffect(() => {
    setPopupPosition(calculatePosition(popupInfo.pixel as string[]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupInfo, index]);

  return (
    <div>
      <div
        className={wrapper}
        ref={popupContainer}
        style={{ ...popupPosition, zIndex: 10000 }}
      >
        <>
          {getFeatures()}
          {getCoords()}
        </>
      </div>
    </div>

  );
}

export default FeaturesPopup;
